import { Icon } from '@festina-life/ui/structure'
import { type FC, type ReactNode, useState } from 'react'

import { EditorButtonGroup } from '../editorButtonGroup/EditorButtonGroup'
import { HoverEditButton } from '../hoverEditGroup/HoverEditButton'

type BaseInputProps<V> = {
  value: V
  onChange?: (value: V) => void
}

type Props<V, P extends BaseInputProps<V>> = {
  onSave: ((newValue: V) => void) | undefined
  renderFn?: (value: V) => ReactNode
  FormInput: FC<P>
  inputProps: P
}

export const EditableFormInput = <V, P extends BaseInputProps<V>>({
  onSave,
  renderFn,
  FormInput,
  inputProps,
}: Props<V, P>) => {
  const [isEditing, setIsEditing] = useState(false)
  const [updatedValue, setUpdatedValue] = useState(inputProps.value)

  const handleSaveContent = () => {
    setIsEditing(false)
    onSave?.(updatedValue)
  }

  const handleCancelEditing = () => {
    setUpdatedValue(inputProps.value)
    setIsEditing(false)
  }

  return (
    <div
      className={`relative [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4 ${
        !!onSave && !isEditing
          ? 'group rounded-md border border-gray-100 p-2'
          : ''
      }`}
    >
      {isEditing ? (
        <>
          <FormInput
            {...inputProps}
            value={updatedValue}
            onChange={(nextValue) => setUpdatedValue(nextValue)}
          />
          <EditorButtonGroup
            onCancel={handleCancelEditing}
            onSave={handleSaveContent}
            className="right-2 z-10 mt-2"
          />
        </>
      ) : (
        <>
          <div className="min-h-[1.5em]">
            {renderFn
              ? renderFn(inputProps.value)
              : JSON.stringify(inputProps.value)}
          </div>
          {!!onSave && (
            <div className="invisible absolute bottom-1 right-2 flex flex-row gap-x-2 group-hover:visible">
              <HoverEditButton
                icon={<Icon name="Pencil" />}
                onClick={() => setIsEditing(true)}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
